import Link from "next/link"
import { Play, ArrowRight } from "lucide-react"

export function Footer() {
  return (
    <footer className="bg-[oklch(0.13_0.004_270)] text-white py-16 border-t border-white/[0.06]">
      <div className="container mx-auto px-5 max-w-6xl">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 mb-12">
          {/* Logo + Description */}
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-6">
              <div className="bg-[#CC3E21] p-1.5 rounded-lg">
                <Play className="w-4 h-4 text-white fill-white ml-0.5" />
              </div>
              <span className="font-display font-bold text-lg text-white">Transcriptr</span>
            </div>
            <p className="text-white/60 text-sm mb-6 max-w-xs">
              The AI video clipping and content automation platform. Turn one video into viral clips published everywhere.
            </p>
            <div className="flex gap-4">
              <a
                href="https://twitter.com/transcriptr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-white/[0.05] rounded-full flex items-center justify-center hover:bg-[#CC3E21] cursor-pointer transition-colors group"
              >
                <span className="sr-only">Twitter</span>
                <svg fill="currentColor" viewBox="0 0 24 24" className="w-4 h-4 text-white/60 group-hover:text-white">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
                </svg>
              </a>
            </div>
          </div>

          {/* Tools Column */}
          <div>
            <h4 className="font-bold mb-4 text-sm uppercase tracking-wider text-white/70">Tools</h4>
            <ul className="space-y-2 text-sm text-white/60">
              <li><Link href="/tools/youtube-transcript-cleaner" className="hover:text-[#ff6b4a] transition-colors">Clean Transcript</Link></li>
              <li><Link href="/tools/youtube-summary-generator" className="hover:text-[#ff6b4a] transition-colors">Summaries</Link></li>
              <li><Link href="/tools/youtube-key-insights" className="hover:text-[#ff6b4a] transition-colors">Key Insights</Link></li>
              <li><Link href="/tools/youtube-to-flashcards" className="hover:text-[#ff6b4a] transition-colors">Flashcards</Link></li>
              <li><Link href="/tools/youtube-to-blog-post" className="hover:text-[#ff6b4a] transition-colors">Blog Articles</Link></li>
              <li><Link href="/tools/youtube-to-twitter-thread" className="hover:text-[#ff6b4a] transition-colors">Twitter Threads</Link></li>
              <li>
                <Link href="/tools" className="inline-flex items-center gap-1 text-[#ff6b4a] hover:text-white transition-colors font-medium">
                  All Tools <ArrowRight className="w-3 h-3" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Guides Column */}
          <div>
            <h4 className="font-bold mb-4 text-sm uppercase tracking-wider text-white/70">Guides</h4>
            <ul className="space-y-2 text-sm text-white/60">
              <li><Link href="/guides/youtube-summary-generator" className="hover:text-[#ff6b4a] transition-colors">Summary Guide</Link></li>
              <li><Link href="/guides/youtube-to-flashcards" className="hover:text-[#ff6b4a] transition-colors">Flashcards Guide</Link></li>
              <li><Link href="/guides/youtube-cornell-notes" className="hover:text-[#ff6b4a] transition-colors">Cornell Notes Guide</Link></li>
              <li><Link href="/guides/youtube-to-blog-post" className="hover:text-[#ff6b4a] transition-colors">Blog Post Guide</Link></li>
              <li><Link href="/guides/youtube-to-twitter-thread" className="hover:text-[#ff6b4a] transition-colors">Twitter Thread Guide</Link></li>
              <li>
                <Link href="/guides" className="inline-flex items-center gap-1 text-[#ff6b4a] hover:text-white transition-colors font-medium">
                  All Guides <ArrowRight className="w-3 h-3" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources Column */}
          <div>
            <h4 className="font-bold mb-4 text-sm uppercase tracking-wider text-white/70">Resources</h4>
            <ul className="space-y-2 text-sm text-white/60">
              <li><Link href="/pricing" className="hover:text-[#ff6b4a] transition-colors">Pricing</Link></li>
              <li><Link href="/contact" className="hover:text-[#ff6b4a] transition-colors">Contact</Link></li>
            </ul>

            <h4 className="font-bold mb-4 mt-8 text-sm uppercase tracking-wider text-white/70">Legal</h4>
            <ul className="space-y-2 text-sm text-white/60">
              <li><Link href="/privacy" className="hover:text-[#ff6b4a] transition-colors">Privacy Policy</Link></li>
              <li><Link href="/terms" className="hover:text-[#ff6b4a] transition-colors">Terms of Service</Link></li>
            </ul>
          </div>

          {/* Compare Column */}
          <div>
            <h4 className="font-bold mb-4 text-sm uppercase tracking-wider text-white/70">Compare</h4>
            <ul className="space-y-2 text-sm text-white/60">
              <li><Link href="/compare/transcriptr-vs-opusclip" className="hover:text-[#ff6b4a] transition-colors">vs OpusClip</Link></li>
              <li><Link href="/compare/transcriptr-vs-vizard" className="hover:text-[#ff6b4a] transition-colors">vs Vizard</Link></li>
              <li><Link href="/compare/transcriptr-vs-klap" className="hover:text-[#ff6b4a] transition-colors">vs Klap</Link></li>
              <li><Link href="/compare/transcriptr-vs-descript" className="hover:text-[#ff6b4a] transition-colors">vs Descript</Link></li>
              <li><Link href="/compare/transcriptr-vs-kapwing" className="hover:text-[#ff6b4a] transition-colors">vs Kapwing</Link></li>
              <li><Link href="/compare/transcriptr-vs-submagic" className="hover:text-[#ff6b4a] transition-colors">vs Submagic</Link></li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/[0.06] flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-white/40 text-sm">&copy; {new Date().getFullYear()} Transcriptr. All rights reserved.</div>
          <div className="flex gap-6 text-sm font-medium text-white/40">
            <Link href="/tools" className="hover:text-[#ff6b4a] transition-colors">Tools</Link>
            <Link href="/guides" className="hover:text-[#ff6b4a] transition-colors">Guides</Link>
            <Link href="/pricing" className="hover:text-[#ff6b4a] transition-colors">Pricing</Link>
            <Link href="/contact" className="hover:text-[#ff6b4a] transition-colors">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  )
}
